import React from "react";
import { Box, Button, IconButton, Stack, Typography } from "@mui/material";
import { X } from "lucide-react";
import { useWebSocketContext } from "components/context/SocketConsumerProvider";
import { VideoCallContext } from "components/context/VideoCallContext";
import { VideoComment } from "protogen/video_pb";
import { postVideoComment, getVideoComments } from "services/video";
import SimpleRichEditor, { Handle } from "components/editor/SimpleRichEditor";
import DocumentViewer from "components/editor/DocumentViewer";
import { RichContent } from "components/editor/utils";
import DateDisplay from "components/common/DateDisplay";

interface VideoCommentsPanelProps {
  open: boolean;
  onClose: () => void;
  videoRoomUuid: string;
}

const VideoCommentEntry: React.FC<{
  entry: VideoComment;
}> = ({ entry }) => {
  const time = new Date(Number(entry.timestampSec) * 1000);

  return (
    <Box
      sx={{
        px: 1.5,
        py: 1,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "baseline",
          gap: 1,
          mb: 0.5,
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 600, color: "white" }}>
          {entry.participantName}
        </Typography>
        <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.6)" }}>
          <DateDisplay date={time} />
        </Typography>
      </Box>
      <Box
        sx={{
          "& .ProseMirror": {
            color: "white !important",
            fontSize: "0.9rem",
            padding: "0 !important",
            minHeight: "unset",
          },
          "& .ProseMirror p": {
            margin: "0.25em 0",
          },
          "& .ProseMirror p:first-of-type": {
            marginTop: 0,
          },
          "& .ProseMirror p:last-of-type": {
            marginBottom: 0,
          },
        }}
      >
        {entry.body?.payload ? (
          <DocumentViewer
            content={JSON.parse(entry.body.payload)}
            attachments={entry.body.attachments}
            minHeight="20px"
          />
        ) : (
          <Typography sx={{ color: "white", fontSize: "0.9rem" }}>
            {entry.body?.textContent || ""}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

const VideoCommentsPanel: React.FC<VideoCommentsPanelProps> = ({
  open,
  onClose,
  videoRoomUuid,
}) => {
  const [comments, setComments] = React.useState<VideoComment[]>([]);
  const [content, setContent] = React.useState<RichContent | null>(null);
  const [isSending, setIsSending] = React.useState(false);
  const [uploadsInProgress, setUploadsInProgress] = React.useState(false);
  const [editorKey, setEditorKey] = React.useState(0);
  const messagesRef = React.useRef<HTMLDivElement>(null);
  const editorRef = React.useRef<Handle>(null);
  const { registerHandler, unregisterHandler } = useWebSocketContext();
  const { videoCallState } = React.useContext(VideoCallContext);

  React.useEffect(() => {
    const loadComments = async () => {
      if (!videoCallState.token) {
        return;
      }
      const response = await getVideoComments(
        { videoRoomUuid },
        videoCallState.token,
      );
      setComments(response.comments);
    };
    if (open) {
      loadComments();
    }
  }, [open, videoRoomUuid, videoCallState.token]);

  React.useEffect(() => {
    const handleNewComment = (channel: string, data: any) => {
      const newComment = data.message_payload as VideoComment;
      setComments((prev) => [...prev, newComment]);
    };

    const channelPrefix = `video-comment-${videoRoomUuid}`;
    registerHandler(channelPrefix, handleNewComment);

    return () => {
      unregisterHandler(channelPrefix, handleNewComment);
    };
  }, [videoRoomUuid, registerHandler, unregisterHandler]);

  React.useEffect(() => {
    if (!open) {
      return;
    }

    messagesRef.current?.scrollTo({
      top: messagesRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [comments, open]);

  const handleSubmit = async () => {
    if (!content || !videoCallState.token) {
      return;
    }

    setIsSending(true);
    const response = await postVideoComment(
      {
        videoRoomUuid,
        body: {
          contentType: "json",
          payload: content.json,
          textContent: content.text,
          attachments: content.attachments,
        },
      },
      videoCallState.token,
    );
    if (response.comment) {
      setComments((prev) => [...prev, response.comment!]);
    }
    setContent(null);
    setEditorKey((prev) => prev + 1);
    setIsSending(false);
  };

  return (
    <Box
      sx={{
        width: open ? 320 : 0,
        opacity: open ? 1 : 0,
        pointerEvents: open ? "auto" : "none",
        transition: "width 0.2s ease, opacity 0.2s ease",
        overflow: "hidden",
        alignSelf: "stretch",
        display: "flex",
      }}
    >
      <Box
        sx={{
          width: 320,
          height: "100%",
          backgroundColor: "rgba(20, 20, 20, 0.92)",
          color: "white",
          borderLeft: "1px solid rgba(255, 255, 255, 0.12)",
          display: "flex",
          flexDirection: "column",
          backdropFilter: "blur(14px)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
            py: 1.5,
            borderBottom: "1px solid rgba(255, 255, 255, 0.12)",
          }}
        >
          <Box>
            <Typography variant="h6">Comments</Typography>
            <Typography
              variant="caption"
              sx={{ color: "rgba(255,255,255,0.7)" }}
            >
              Shared with everyone in the room.
            </Typography>
          </Box>
          <IconButton onClick={onClose} sx={{ color: "white" }}>
            <X size={18} />
          </IconButton>
        </Box>

        <Box sx={{ flex: 1, minHeight: 0, px: 1.5, py: 1 }}>
          {comments.length === 0 ? (
            <Stack
              spacing={1}
              sx={{
                height: "100%",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                color: "rgba(255,255,255,0.72)",
                px: 2,
              }}
            >
              <Typography variant="body2">No comments yet.</Typography>
              <Typography variant="caption">
                Send a note to everyone currently in the call.
              </Typography>
            </Stack>
          ) : (
            <Box
              ref={messagesRef}
              sx={{
                height: "100%",
                overflowY: "auto",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {comments.map((entry) => (
                <VideoCommentEntry key={entry.ref} entry={entry} />
              ))}
            </Box>
          )}
        </Box>

        <Box
          sx={{
            p: 1.5,
            borderTop: "1px solid rgba(255, 255, 255, 0.12)",
          }}
        >
          <SimpleRichEditor
            key={editorKey}
            ref={editorRef}
            disabled={isSending}
            setContent={setContent}
            placeholder="Add a comment..."
            setUploadsInProgress={setUploadsInProgress}
            editorMinHeight="60px"
            editorMaxHeight="200px"
            inputSx={{
              backgroundColor: "rgba(255,255,255,0.08)",
              "& .ProseMirror": {
                color: "white",
                padding: "8px 12px",
              },
              "& .ProseMirror p.is-editor-empty:first-of-type::before": {
                color: "rgba(255,255,255,0.5)",
              },
            }}
            primaryAction={
              <Button
                size="small"
                onClick={handleSubmit}
                disabled={isSending || !content || uploadsInProgress}
              >
                Send
              </Button>
            }
          />
        </Box>
      </Box>
    </Box>
  );
};

export default VideoCommentsPanel;
